import React, {useEffect, useState} from "react";
import withRouter from "components/Common/withRouter";
import {useDispatch, useSelector} from "react-redux";
import {Row, Col, Popover, PopoverBody, Pagination, PaginationItem, PaginationLink} from "reactstrap";
import { Link } from 'react-router-dom';
import CardItem from "components/PurpleTheme/CardItem";
import '../../pages/Service/services.scss';

import {getLocalDateTime} from "../../helpers/component_helpers";
import {getAllDaemons} from "../../store/daemon/actions";

const DaemonTable = () => {
  const [databaseConnections, setDatabaseConnections] = useState({});
  const [currentHoverItem, setCurrentHoverItem] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getAllDaemons())
  }, [])

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    const pageFilters = {
      page: pageNumber,
      limit: itemsPerPage
    }
    dispatch(getAllDaemons(pageFilters));
  }

  const {daemon_details} = useSelector(state => ({
    daemon_details: state.Daemon.daemon_details
  }));

  const isRecentlySeen = (last_seen) => {
    const now = Date.now();
    const lastSeen = new Date(last_seen).getTime();
    return (now - lastSeen) <= 120000;
  };

  function toggleDatabaseConnectionsTooltip(master_lambda_id, isOpen) {
    setDatabaseConnections(prevState => ({
      ...prevState,
      [master_lambda_id]: isOpen,
    }));
  }

  const headers = [
    {name:"Daemon ID", width: 3},
    {name:"Monitored Resource", width: 3},
    {name:"Last Ping", width: 3},
    {name:"External Connections", width: 2},
    {name:"Status", width: 1}
  ]

  const totalPages = Math.ceil((0) / itemsPerPage); //TODO: Added resource count here
  let startPage = Math.max(currentPage - 1, 1);
  let endPage = Math.min(startPage + 2, totalPages);

  return (
    <React.Fragment>
      {daemon_details?.loading ? (
        <div className="w-full d-flex justify-content-center align-items-center">
          <div role="status" className="ms-2 spinner-border text-primary">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <>
          <div className={'d-flex justify-content-end align-items-center mb-3'}>
            {
              daemon_details?.daemons?.length > 0 ?
                <Link
                  to={'/daemon-lambdas'}
                  className="btn btn-primary me-3"
                >
                  Manage Lambdas
                </Link>
                : null}
                <>
                  <Link
                    to={'/daemon-onboarding'}
                    className="btn btn-primary me-3"
                    target={'_blank'}
                  >
                    Onboard Daemons
                  </Link>
                </>
            <div>
              <Pagination
                aria-label="pagination navigation"
                listClassName="justify-content-end pagination-rounded pt-2"
                className="mt-2"
              >
                <PaginationItem
                  key={"pagination-item-" + Math.max((currentPage - 1 ||  1), 1)}
                  disabled={currentPage === 1}
                >
                  <PaginationLink
                    href="#"
                    onClick={() => handlePageChange(Math.max((currentPage - 1 ||  1), 1))}>
                    <i className="mdi mdi-chevron-left"/>
                  </PaginationLink>
                </PaginationItem>
                {[...Array(endPage - startPage + 1)].map((_, index) => (
                  <PaginationItem key={"pagination-item-" + index + (startPage + index)}
                                  active={currentPage === startPage + index}>
                    <PaginationLink href="#" onClick={() => handlePageChange(startPage + index)}>
                      {startPage + index}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem key={"pagination-item" + Math.min(currentPage + 1, totalPages)}
                                disabled={currentPage === totalPages}>
                  <PaginationLink
                    href="#"
                    onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}>
                    <i className="mdi mdi-chevron-right"/>
                  </PaginationLink>
                </PaginationItem>
              </Pagination>
            </div>
          </div>
          <div className="card-container">
            <div className="header">
              <Row className="text-center w-100">
                {headers.map((header, index) => (
                  <Col md={header.width} lg={header.width} key={index}>
                    <div className="d-flex align-items-center justify-content-center p-2">
                      {header.name}
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
            {daemon_details?.daemons?.map((daemon, index) => (
              <React.Fragment key={daemon.id}>
                <CardItem
                  className="card-item"
                  bgColor={currentHoverItem === daemon.id ? '#382d45' : ''}
                  cursor="pointer"
                  onMouseEnter={() => setCurrentHoverItem(daemon.id)}
                  onMouseLeave={() => setCurrentHoverItem(null)}
                >
                  <Row className="text-center w-100">
                    <Col lg={headers[0].width}>
                      <div className="d-flex align-items-center justify-content-center p-2">
                        <Link to={`/daemon/${daemon.id}`}>
                          {daemon.id}
                        </Link>
                      </div>
                    </Col>
                    <Col lg={headers[1].width}>
                      <div className="d-flex align-items-center justify-content-center p-2">
                        <Link to={`/resource/${daemon.monitored_resource_id}/alerts`} target="_blank">
                          {daemon.monitored_resource_name}
                          <i className="bx bx-link-external font-size-16"/>
                        </Link>
                      </div>
                    </Col>
                    <Col lg={headers[2].width}>
                      <div className="d-flex align-items-center justify-content-center p-2">
                        {getLocalDateTime(daemon.last_seen)}
                      </div>
                    </Col>
                    <Col lg={headers[3].width}>
                      <div
                        id={`db_connections_${daemon.id}`}
                      >
                        {daemon.database_connections.length === 0 ?
                          null :
                          <>
                            <i
                              className="mdi mdi-database font-size-16 text-primary"
                              onMouseEnter={() => toggleDatabaseConnectionsTooltip(daemon.id, true)}
                              onMouseLeave={() => toggleDatabaseConnectionsTooltip(daemon.id, false)}
                              style={{cursor: 'pointer'}}
                            />
                            <Popover
                              placement="top"
                              isOpen={databaseConnections[daemon.id] || false}
                              target={`db_connections_${daemon.id}`}
                            >
                              <PopoverBody>
                                Available Database Connections:
                                <ul
                                  className="ms-0">
                                  {daemon.database_connections.map((connection, index) => (
                                    <li key={index}>
                                      {connection}
                                    </li>
                                  ))}
                                </ul>
                              </PopoverBody>
                            </Popover>
                          </>
                        }
                      </div>
                    </Col>
                    <Col lg={headers[4].width}>
                      <div className="d-flex align-items-center justify-content-center p-2">
                        <span className={`badge rounded-pill badge-soft-${daemon.online ? 'success' : 'danger'}`}>
                          {daemon.online ? 'Online' : 'Offline'}
                        </span>
                      </div>
                      <div
                      >
                      </div>
                    </Col>
                  </Row>
                </CardItem>
              </React.Fragment>
            ))}
          </div>
          <h6 style={{textAlign: 'right', marginTop: '5px', marginBottom:'20px'}}>Showing {(daemon_details?.daemons?.length || 0)} of {daemon_details?.count} Results</h6>
        </>
      )}
    </React.Fragment>
  );
};

export default withRouter(DaemonTable);
